// =============================================================================
// BulkImportDialog.jsx
//
// Upload -> Preview -> Commit flow for the Activity Tracker "Bulk Import".
// Preview never writes to the DB — it only parses the file, normalises
// Technology/Vendor via the Template's aliases, and resolves JV_ID -> CSR.
// The user reviews/prunes the resolved sites here before confirming.
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Box, Typography, Divider, Alert,
  TextField, MenuItem, Select, InputLabel, FormControl,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Checkbox, Chip, CircularProgress, FormControlLabel,
} from '@mui/material';
import CloseIcon      from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { apiGetAvailableTemplates, apiPreviewImport, apiCommitImport, apiGetUploaderDefaults } from '../requests/importRequests.js';
import { apiGetFeatures, apiGetTeams, apiGetOwnersByTeam, apiGetVendors, apiGetTechnologies } from '../requests/trackerRequests.js';

const cellSx = { fontSize: '0.78rem', py: 0.6 };
const headSx = {
  fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', bgcolor: 'primary.main', py: 0.9, borderBottom: 'none',
};

const emptyFields = () => ({
  title: '', vendor: '', technology: '', comments: '', featureId: '',
  teamId: '', ownerIds: [],
  startDate: new Date().toISOString().slice(0, 10), endDate: '',
});

const BulkImportDialog = ({ open, onClose, onImported }) => {
  const [step, setStep]           = useState('upload');   // 'upload' | 'preview'
  const [templates, setTemplates] = useState([]);
  const [features, setFeatures]   = useState([]);
  const [teams, setTeams]         = useState([]);
  const [vendors, setVendors]         = useState([]);   // [{ id, name }] — TrackerVendor
  const [technologies, setTechnologies] = useState([]); // [{ id, name }] — TrackerTechnology
  const [owners, setOwners]       = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  // Team/Owner por omissão para quem está a fazer o upload (ver
  // ActivityImportController.GetUploaderDefaults) — aplicados assim que o
  // Preview resolve, para o caso comum de o uploader ser também o Owner.
  const [uploaderDefaults, setUploaderDefaults] = useState({ teamId: null, ownerId: null });
  const [templateId, setTemplateId] = useState('');
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [fields, setFields]       = useState(emptyFields());
  const [matches, setMatches]     = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError]         = useState(null);
  // Preferência do utilizador — não é reposta pelo reset() para persistir
  // entre importações sucessivas dentro da mesma sessão.
  const [autoOpen, setAutoOpen]   = useState(true);

  const reset = useCallback(() => {
    setStep('upload');
    setTemplateId('');
    setFile(null);
    setPreview(null);
    setFields(emptyFields());
    setMatches([]);
    setOwners([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    apiGetAvailableTemplates().then((res) => setTemplates(res.data ?? [])).catch(() => {});
    apiGetFeatures().then((res) => setFeatures(res.data ?? [])).catch(() => {});
    apiGetTeams().then((res) => setTeams(res.data ?? [])).catch(() => {});
    apiGetVendors().then((res) => setVendors(res.data ?? [])).catch(() => {});
    apiGetTechnologies().then((res) => setTechnologies(res.data ?? [])).catch(() => {});
    apiGetUploaderDefaults()
      .then((res) => setUploaderDefaults({
        teamId: res.data?.teamId ?? null,
        ownerId: res.data?.ownerId ?? null,
      }))
      .catch(() => setUploaderDefaults({ teamId: null, ownerId: null }));
  }, [open, reset]);

  // Owners são dependentes da Team escolhida — mesma cascata do TrackerModal.
  useEffect(() => {
    if (!fields.teamId) { setOwners([]); return; }
    setLoadingOwners(true);
    apiGetOwnersByTeam(fields.teamId)
      .then((res) => setOwners(res.data ?? []))
      .catch(() => setOwners([]))
      .finally(() => setLoadingOwners(false));
  }, [fields.teamId]);

  const handleTeamChange = (e) => {
    const teamId = e.target.value;
    setFields((p) => ({ ...p, teamId, ownerIds: [] }));   // owners dependem da team — reset ao trocar
  };

  const handleOwnerChange = (e) => {
    const value = e.target.value;
    setFields((p) => ({ ...p, ownerIds: Array.isArray(value) ? value : [value] }));
  };

  const allOwnerIds = owners.map((o) => String(o.id));
  const allOwnersSelected = allOwnerIds.length > 0 && allOwnerIds.every((id) => fields.ownerIds.includes(id));

  // Botão próprio (fora do menu do Select) — ver comentário junto ao JSX.
  const handleToggleAllOwners = () => {
    setFields((p) => ({ ...p, ownerIds: allOwnersSelected ? [] : allOwnerIds }));
  };

  const handleClose = () => { reset(); onClose?.(); };

  const handlePreview = () => {
    if (!templateId || !file) {
      setError('Pick a Side and choose a file first.');
      return;
    }
    setLoadingPreview(true);
    setError(null);
    apiPreviewImport(templateId, file)
      .then((res) => {
        const p = res.data;
        setPreview(p);
        setFields({
          title: p.title ?? '',
          // Vendor/Technology já vêm canonicalizados pelo backend (batem
          // certo com o TrackerVendor/TrackerTechnology correspondente) —
          // ver ActivityImportService.PreviewAsync.
          vendor: p.vendor ?? '',
          technology: p.technology ?? '',
          comments: p.comments ?? '',
          featureId: p.featureId ?? '',
          // Pré-preenche com o Team/Owner de quem está a fazer o upload,
          // quando existirem — o utilizador continua livre para os trocar.
          teamId: uploaderDefaults.teamId != null ? String(uploaderDefaults.teamId) : '',
          ownerIds: uploaderDefaults.ownerId != null ? [String(uploaderDefaults.ownerId)] : [],
          startDate: new Date().toISOString().slice(0, 10),
          endDate: '',
        });
        setMatches((p.matches ?? []).map((m) => ({ ...m })));
        setStep('preview');
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to preview this file.'))
      .finally(() => setLoadingPreview(false));
  };

  const toggleMatch = (idx) => {
    setMatches((prev) => prev.map((m, i) => (i === idx ? { ...m, selected: !m.selected } : m)));
  };

  const handleCommit = () => {
    if (!matches.some((m) => m.selected)) {
      setError('At least one resolved site must stay selected.');
      return;
    }
    if (!fields.teamId) {
      setError('Team is required.');
      return;
    }
    if (!fields.ownerIds?.length) {
      setError('At least one Activity Owner is required.');
      return;
    }
    setCommitting(true);
    setError(null);
    apiCommitImport({
      templateId: Number(templateId),
      fileName: file?.name ?? '',
      title: fields.title,
      vendor: fields.vendor,
      technology: fields.technology,
      comments: fields.comments,
      featureId: fields.featureId === '' ? null : Number(fields.featureId),
      teamId: Number(fields.teamId),
      ownerIds: fields.ownerIds.map(Number),
      startDate: fields.startDate || null,
      endDate: fields.endDate || null,
      rowCount: preview?.rowCount ?? 0,
      matches,
      unmatchedJvIds: preview?.unmatchedJvIds ?? [],
    })
      .then((res) => { onImported?.({ ...res?.data, autoOpen }); handleClose(); })
      .catch((err) => setError(
        (err?.response?.data?.errors && err.response.data.errors.join(', '))
        || err?.response?.data?.message
        || 'Failed to create the activity.'
      ))
      .finally(() => setCommitting(false));
  };

  const selectedCount = matches.filter((m) => m.selected).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700} fontSize="1.05rem">Bulk Import</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {step === 'upload' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Side</InputLabel>
              <Select label="Side" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.side} — {t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ alignSelf: 'flex-start' }}>
              {file ? file.name : 'Choose file (.csv or .xlsx)'}
              <input type="file" hidden accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Button>

            <Typography variant="caption" color="text.secondary">
              The file is only parsed and matched against the network database here — nothing is saved until you confirm on the next step.
            </Typography>
          </Box>
        )}

        {step === 'preview' && preview && (
          <Box>
            {preview.warnings?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField size="small" label="Title" value={fields.title}
                onChange={(e) => setFields((p) => ({ ...p, title: e.target.value }))} sx={{ minWidth: 260 }} />
              {/* Vendor/Technology — dropdown a partir dos picklists (TrackerVendor/
                  TrackerTechnology), tal como o Feature logo a seguir. Já não são
                  texto livre: o backend só deixa chegar aqui um valor que bate
                  certo com um destes picklists (canonicalizado, ex: "HUAWEI" ->
                  "Huawei"); se o ficheiro trouxer um Vendor/Technology
                  inexistente, o Preview falha com um erro em vez de deixar
                  criar uma atividade com um valor inválido/mal capitalizado. */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Vendor</InputLabel>
                <Select label="Vendor" value={fields.vendor}
                  onChange={(e) => setFields((p) => ({ ...p, vendor: e.target.value }))}>
                  {vendors.map((v) => <MenuItem key={v.id} value={v.name}>{v.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Technology</InputLabel>
                <Select label="Technology" value={fields.technology}
                  onChange={(e) => setFields((p) => ({ ...p, technology: e.target.value }))}>
                  {technologies.map((t) => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Feature</InputLabel>
                <Select label="Feature" value={fields.featureId}
                  onChange={(e) => setFields((p) => ({ ...p, featureId: e.target.value }))}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {features.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                value={fields.startDate} onChange={(e) => setFields((p) => ({ ...p, startDate: e.target.value }))} />
              <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
                value={fields.endDate} onChange={(e) => setFields((p) => ({ ...p, endDate: e.target.value }))} />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Team *</InputLabel>
                <Select label="Team *" value={fields.teamId} onChange={handleTeamChange}>
                  {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                <FormControl size="small" sx={{ minWidth: 220 }} disabled={!fields.teamId || loadingOwners}>
                  <InputLabel>Activity Owner *</InputLabel>
                  <Select
                    label="Activity Owner *"
                    multiple
                    value={fields.ownerIds}
                    onChange={handleOwnerChange}
                    renderValue={(selected) => selected
                      .map((id) => owners.find((o) => String(o.id) === String(id))?.name)
                      .filter(Boolean)
                      .join(', ') || (fields.teamId ? 'Select owner(s)' : 'Select a team first')}
                  >
                    {owners.length === 0 && (
                      <MenuItem disabled value="">
                        <em>{fields.teamId ? (loadingOwners ? 'Loading…' : 'No owners available') : 'Select a team first'}</em>
                      </MenuItem>
                    )}
                    {owners.map((o) => (
                      <MenuItem key={o.id} value={String(o.id)}>
                        <Checkbox checked={fields.ownerIds.includes(String(o.id))} size="small" sx={{ mr: 1, p: 0 }} />
                        {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Botão fora do menu do Select — mais fiável do que um "Select All"
                    dentro da lista (o Select intercepta o clique dos MenuItems
                    internamente, o que dava resultados inconsistentes). */}
                <Button size="small" onClick={handleToggleAllOwners} disabled={owners.length === 0}
                  sx={{ whiteSpace: 'nowrap', mb: 0.25 }}>
                  {allOwnersSelected ? 'Clear' : 'Select all'}
                </Button>
              </Box>
              <TextField size="small" label="Comments" value={fields.comments} multiline maxRows={3}
                onChange={(e) => setFields((p) => ({ ...p, comments: e.target.value }))} sx={{ minWidth: '100%' }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontWeight={600} fontSize="0.82rem">
                Resolved sites ({selectedCount} of {matches.length} selected)
              </Typography>
              {preview.unmatchedJvIds?.length > 0 && (
                <Chip size="small" color="warning" label={`${preview.unmatchedJvIds.length} JV_ID(s) unmatched`} />
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headSx, width: 46 }} />
                    <TableCell sx={headSx}>JV_ID</TableCell>
                    <TableCell sx={headSx}>OMC_ID</TableCell>
                    <TableCell sx={headSx}>TAC</TableCell>
                    <TableCell sx={headSx}>Site (CSR)</TableCell>
                    <TableCell sx={headSx}>Side</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matches.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No sites resolved.</TableCell></TableRow>
                  ) : matches.map((m, idx) => (
                    <TableRow
                      key={`${m.jvId}-${m.omcId}-${m.tac}-${m.csr}-${idx}`}
                      onClick={() => toggleMatch(idx)}
                      sx={{
                        opacity: m.selected ? 1 : 0.4,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {/* onClick + stopPropagation na célula: clicar em qualquer
                          parte da linha alterna a selecção (alvo maior, mais
                          fácil de acertar do que só a checkbox), mas sem
                          disparar DUAS vezes quando o clique cai mesmo em cima
                          da checkbox (o próprio onChange dela já trata disso). */}
                      <TableCell sx={cellSx} padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={m.selected} onChange={() => toggleMatch(idx)} />
                      </TableCell>
                      <TableCell sx={cellSx}>{m.jvId}</TableCell>
                      <TableCell sx={cellSx}>{m.omcId}</TableCell>
                      <TableCell sx={cellSx}>{m.tac}</TableCell>
                      <TableCell sx={cellSx}>{m.csr}</TableCell>
                      <TableCell sx={cellSx}>{m.side}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {preview.unmatchedJvIds?.length > 0 && (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                No match found for: {preview.unmatchedJvIds.join(', ')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Box>
          {step === 'preview' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={autoOpen}
                  onChange={(e) => setAutoOpen(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Auto-open after creation
                </Typography>
              }
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {step === 'upload' ? (
            <>
              <Button onClick={handleClose} color="inherit">Cancel</Button>
              <Button variant="contained" onClick={handlePreview} disabled={loadingPreview}>
                {loadingPreview ? <CircularProgress size={18} /> : 'Preview'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setStep('upload')} color="inherit" disabled={committing}>Back</Button>
              <Button variant="contained" onClick={handleCommit} disabled={committing}>
                {committing ? <CircularProgress size={18} /> : 'Confirm & Create Activity'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;
